import PropTypes from 'prop-types';
import {useState, useCallback} from 'react';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {Form, Grid, Popup, Icon, Button} from 'semantic-ui-react';

import {getChangedFieldsValues} from 'forms';

import {subdomainOptions, buildSubdomainDropdownOptions} from '../../Common/sharedElements';
import QrCodeModal from '../../QrCode/QrCodeModal';

import ChangedHostWarning from './ChangedHostWarning';

import styles from './GitlabPages.module.scss';

const SiteLink = ({host}) => {
  return (
    <Popup
      trigger={
        <a
          href={`https://${host.name}${host.subdomain}`}
          target="_blank"
          className={styles.SiteLink}
          rel="noreferrer"
        >
          <Icon name="external" />
        </a>
      }
      position="right center"
      content="View site"
    />
  );
};

SiteLink.propTypes = {
  host: PropTypes.object,
};

const CustomHostInput = ({host}) => {
  return (
    <Grid.Row>
      <Grid.Column width={9}>
        <Form.Input disabled value={host.name} aria-label="Site name" />
      </Grid.Column>
      <Grid.Column width={7} className={styles.Subdomain}>
        <Grid>
          <Grid.Row>
            <Form.Input
              disabled
              value={host.subdomain}
              fluid
              className={styles.SubdomainDropdown}
              aria-label="Subdomain"
            />
            <Grid.Column>
              <Popup
                position="right center"
                hoverable
                content={
                  <div>
                    This is a custom URL. If you want to edit it please, open a{' '}
                    <a
                      href="https://cern.service-now.com/service-portal?id=sc_cat_item&name=request&se=gitlab-pages"
                      target="_blank"
                      rel="noreferrer"
                    >
                      SNOW Request
                    </a>
                    .
                  </div>
                }
                trigger={<Icon name="info" style={{marginTop: '0.75rem'}} />}
              />
            </Grid.Column>
            <Grid.Column className={styles.ManageHostButton}>
              <SiteLink host={host} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Grid.Column>
    </Grid.Row>
  );
};

CustomHostInput.propTypes = {
  host: PropTypes.object,
};

const HostsFormInput = ({
  defaultSubdomain = '.docs.cern.ch',
  value,
  processedHosts,
  repositoryUrl,
  name,
}) => {
  const defaultNewHostValues = {
    name: '',
    subdomain: defaultSubdomain,
  };

  const {control, trigger, dirtyFields} = useFormContext();

  let {fields: siteHosts, append, remove} = useFieldArray({
    control,
    name: name,
  });

  // Helper function to calculate when to show the changed host warning
  // It will be shown when removing or adding a host
  const showChangedHostWarningHelper = useCallback(() => {
    if (value?.length !== processedHosts?.length) {
      return true;
    }
    // Necessary to skip the warning when a user just clicks on Add Host and immediatly removes it
    // since it creates and empty field in the object
    const changedHosts = Object.values(
      getChangedFieldsValues(dirtyFields.processedHosts, siteHosts)
    );
    return changedHosts.filter(field => Object.keys(field).length !== 0).length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyFields.processedHosts]);

  // QR Code
  const [qrUrl, setQrUrl] = useState(null);

  return (
    <>
      <Grid columns={3} stackable>
        <Grid.Column textAlign="right" width={3}>
          <div className={styles.FixedLabel}>
            <Popup
              content="Choose a descriptive and short name, following naming conventions."
              position="left center"
              trigger={<label>Site URL</label>}
            />
          </div>
        </Grid.Column>
        <Grid.Column width={12}>
          <Grid stackable className={styles.SiteUrl}>
            {siteHosts.map((host, index) => {
              if (!subdomainOptions.includes(host.subdomain)) {
                return <CustomHostInput key={index} host={host} />;
              } else {
                return (
                  <Grid.Row key={index}>
                    <Grid.Column width={7}>
                      <Controller
                        key={host.id}
                        control={control}
                        name={`${name}.${index}.name`}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                          <Form.Input
                            name={name}
                            value={value}
                            error={error?.message}
                            onChange={(_evt, data) => {
                              onChange(data.value);

                              // trigger validation of the subdomain field
                              trigger(`${name}.${index}.subdomain`);
                            }}
                            aria-label="Site name"
                          />
                        )}
                      />
                    </Grid.Column>
                    <Grid.Column width={6} className={styles.Subdomain}>
                      <Grid>
                        <Grid.Row>
                          <Controller
                            key={host.id}
                            control={control}
                            name={`${name}.${index}.subdomain`}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                              <Form.Select
                                name={name}
                                value={value}
                                error={error?.message}
                                fluid
                                className={styles.SubdomainDropdown}
                                // Remove this filter once webEOS migration support ends
                                // https://gitlab.cern.ch/webservices/webservices-portal/-/issues/197
                                options={buildSubdomainDropdownOptions().filter(
                                  subdomain => subdomain.key !== '.webtest.cern.ch'
                                )}
                                onChange={(_evt, data) => {
                                  onChange(data.value);

                                  // trigger validation of the name field
                                  trigger(`${name}.${index}.name`);
                                }}
                                aria-label="Subdomain"
                              />
                            )}
                          />
                          {/* Show only the remove button when more than one URL is defined for a GPS site */}
                          {siteHosts.length > 1 && (
                            <Grid.Column className={styles.ManageHostButton}>
                              <Popup
                                trigger={<Icon name="delete" onClick={() => remove(index)} />}
                                position="right center"
                                content="Remove URL"
                              />
                            </Grid.Column>
                          )}
                          {value.includes(host.name + host.subdomain) && (
                            <Grid.Column className={styles.ManageHostButton}>
                              {host.name && <SiteLink host={host} />}
                            </Grid.Column>
                          )}

                          {/* Show QR code icon only if site host name is available */}
                          {host.name && host.subdomain && (
                            <Grid.Column>
                              {!showChangedHostWarningHelper() && (
                                <Popup
                                  trigger={
                                    <Icon
                                      className={styles.QrCodeIcon}
                                      name="qrcode"
                                      onClick={() => {
                                        const currentUrl = `https://${host.name}${host.subdomain}`;
                                        setQrUrl(currentUrl);
                                      }}
                                    />
                                  }
                                  position="top center"
                                >
                                  Generate QR code
                                </Popup>
                              )}
                            </Grid.Column>
                          )}
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                );
              }
            })}
            <Grid.Row className={styles.AddHostButton}>
              <Button onClick={() => append(defaultNewHostValues)}>
                <Icon name="plus" /> Add Host
              </Button>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column width={12}>
          {showChangedHostWarningHelper() && <ChangedHostWarning repositoryUrl={repositoryUrl} />}
        </Grid.Column>
      </Grid>

      {qrUrl && (
        <QrCodeModal
          siteUrl={qrUrl}
          onClose={() => {
            setQrUrl('');
          }}
        />
      )}
    </>
  );
};

HostsFormInput.propTypes = {
  defaultSubdomain: PropTypes.array,
  value: PropTypes.array.isRequired,
  processedHosts: PropTypes.array,
  repositoryUrl: PropTypes.string,
  name: PropTypes.string.isRequired,
};

export default HostsFormInput;
